// src/pages/UserProfile.tsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  UserCircleIcon, 
  MusicalNoteIcon,
  ArrowRightOnRectangleIcon, 
  ArrowPathIcon,
  PlusCircleIcon,
  XMarkIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { 
  isAuthenticated, 
  getCurrentUserName, 
  getCurrentUserId,
  logout,
  fetchUserProfile
} from '../services/authService';
import { getUserLinkedArtists, unlinkArtistFromUser } from '../services/spotifyArtistService';
import { updateArtistDataFromSpotify } from '../services/spotifyService';
import ArtistSearch from '../components/artists/ArtistSearch';
import LoadingState from '../components/common/LoadingState';
import ErrorMessage from '../components/common/ErrorMessage';

const UserProfile: React.FC = () => {
  const navigate = useNavigate();
  const [userName, setUserName] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [linkedArtists, setLinkedArtists] = useState<Array<{id: string, spotifyId: string, name: string}>>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showArtistSearch, setShowArtistSearch] = useState<boolean>(false);
  const [unlinking, setUnlinking] = useState<{status: boolean, artistId: string | null}>({
    status: false,
    artistId: null
  });
  const [unlinkSuccess, setUnlinkSuccess] = useState<boolean>(false);
  
  // Neuer State für Artist-Aktualisierung
  const [updatingArtistId, setUpdatingArtistId] = useState<string | null>(null);
  const [updateStats, setUpdateStats] = useState<{[key: string]: any}>({});

  useEffect(() => {
    const checkAuth = async () => {
      if (!isAuthenticated()) {
        // Redirect to login page if not authenticated
        navigate('/login', { state: { returnPath: '/profile' } });
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Get user details
        setUserName(getCurrentUserName());
        setUserId(getCurrentUserId());
        
        // Get linked artists
        const artists = await getUserLinkedArtists();
        setLinkedArtists(artists);
        
      } catch (err) {
        console.error('Error loading user profile:', err);
        setError('Failed to load user profile. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  // Effekt zum Löschen der Erfolgsmeldung nach 3 Sekunden
  useEffect(() => {
    if (unlinkSuccess) {
      const timer = setTimeout(() => {
        setUnlinkSuccess(false);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [unlinkSuccess]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const refreshUserData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Refresh user profile
      await fetchUserProfile();
      
      // Get updated linked artists
      const artists = await getUserLinkedArtists();
      setLinkedArtists(artists);
      
      // Update local state
      setUserName(getCurrentUserName());
      setUserId(getCurrentUserId());
      
    } catch (err) {
      console.error('Error refreshing user data:', err);
      setError('Failed to refresh user data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleArtistLinked = async () => {
    // Hide artist search
    setShowArtistSearch(false);
    
    // Refresh user data to show newly linked artist
    await refreshUserData();
  };

  const handleUnlinkArtist = async (artist: {id: string, spotifyId: string, name: string}) => {
    try {
      setUnlinking({ status: true, artistId: artist.id });
      setError(null);
      
      const artistId = artist.id || artist.spotifyId;
      console.log("Attempting to unlink artist:", artistId);
      
      // Use the id or spotifyId (whichever is available)
      const success = await unlinkArtistFromUser(artistId);
      
      if (success) {
        setUnlinkSuccess(true);
        // Remove the artist from the local state
        setLinkedArtists(prev => prev.filter(a => a.id !== artist.id && a.spotifyId !== artist.spotifyId));
      } else {
        setError('Failed to unlink artist. Please try again.');
      }
    } catch (err) {
      console.error('Error unlinking artist:', err);
      setError('An error occurred while unlinking the artist.');
    } finally {
      setUnlinking({ status: false, artistId: null });
    }
  };
  
  // Neue Funktion zum Aktualisieren der Künstlerdaten
  const handleUpdateArtist = async (artist: {id: string, spotifyId: string, name: string}) => {
    try {
      setUpdatingArtistId(artist.id);
      setError(null);
      
      // Spotify-ID prüfen
      const spotifyId = artist.spotifyId || artist.id;
      
      // API-Aufruf zum Aktualisieren der Daten
      const result = await updateArtistDataFromSpotify(artist.id, spotifyId);
      
      // Erfolg speichern
      setUpdateStats({
        ...updateStats,
        [artist.id]: result.stats
      });
      
      // Nach kurzer Zeit die Erfolgsmeldung ausblenden
      setTimeout(() => {
        setUpdateStats(prev => {
          const newStats = {...prev};
          delete newStats[artist.id];
          return newStats;
        });
      }, 5000);
      
    } catch (err) {
      console.error('Fehler beim Aktualisieren des Artists:', err);
      setError(`Fehler beim Aktualisieren des Artists: ${artist.name}`);
    } finally {
      setUpdatingArtistId(null);
    }
  };

  if (loading) {
    return <LoadingState message="Loading user profile..." size="large" />;
  }

  if (error) {
    return (
      <ErrorMessage 
        title="Error Loading Profile" 
        message={error} 
        onRetry={refreshUserData}
      />
    );
  }

  return (
    <div className="container mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Your Profile</h1>
        <p className="text-gray-600">Manage your account and linked artists</p>
      </div>

      {unlinkSuccess && (
        <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded relative">
          <span className="block sm:inline">Artist successfully unlinked.</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User Profile Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex flex-col items-center">
            <UserCircleIcon className="h-24 w-24 text-gray-400" />
            <h2 className="mt-4 text-xl font-semibold text-gray-800">{userName || 'Spotify User'}</h2>
            <p className="text-sm text-gray-500">Account ID: {userId}</p>
            
            <div className="mt-6 w-full">
              <button 
                onClick={handleLogout}
                className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2" />
                Sign Out
              </button>
            </div>
          </div>
        </div>

        {/* Linked Artists */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-700">Linked Artist Profiles</h2>
            <div className="flex space-x-2">
              <button
                onClick={refreshUserData}
                className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                disabled={loading}
              >
                <ArrowPathIcon className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={() => setShowArtistSearch(true)}
                className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
              >
                <PlusCircleIcon className="h-4 w-4 mr-1" />
                Link Artist
              </button>
            </div>
          </div>

          {showArtistSearch ? (
            <div className="mt-4">
              <ArtistSearch 
                onArtistSelected={handleArtistLinked} 
                showLinkButton={true}
              />
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setShowArtistSearch(false)}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              {linkedArtists.length > 0 ? (
                <div className="divide-y divide-gray-200">
                  {linkedArtists.map((artist) => (
                    <div key={artist.id || artist.spotifyId} className="p-4 flex items-center justify-between">
                      <div className="flex items-center">
                        <MusicalNoteIcon className="h-6 w-6 text-gray-400 mr-3" />
                        <div>
                          <h3 className="text-sm font-medium text-gray-900">{artist.name}</h3>
                          <p className="text-xs text-gray-500">ID: {artist.spotifyId || artist.id}</p>
                          
                          {/* Erfolgsmeldung anzeigen, wenn vorhanden */}
                          {updateStats[artist.id] && (
                            <p className="text-xs text-green-600 mt-1">
                              Aktualisiert: {updateStats[artist.id].tracks_added} neue Songs, {updateStats[artist.id].releases_added} neue Releases
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        {/* Update-Button */}
                        <button
                          onClick={() => handleUpdateArtist(artist)}
                          disabled={updatingArtistId === artist.id}
                          className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200 disabled:opacity-50"
                        >
                          {updatingArtistId === artist.id ? (
                            <>
                              <span className="animate-spin mr-1 h-3 w-3 border-t-2 border-green-500 rounded-full" />
                              Aktualisiere...
                            </>
                          ) : (
                            <>
                              <ArrowPathIcon className="h-3 w-3 mr-1" />
                              Aktualisieren
                            </>
                          )}
                        </button>
                        
                        <Link
                          to={`/artist/${artist.id}`}
                          className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200"
                        >
                          View Profile
                        </Link>
                        <button
                          onClick={() => handleUnlinkArtist(artist)}
                          disabled={unlinking.status}
                          className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 disabled:opacity-50"
                        >
                          {unlinking.status && unlinking.artistId === artist.id ? (
                            <>
                              <span className="animate-spin mr-1 h-3 w-3 border-t-2 border-red-500 rounded-full" />
                              Unlinking...
                            </>
                          ) : (
                            <>
                              <TrashIcon className="h-3 w-3 mr-1" />
                              Unlink
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <MusicalNoteIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <h3 className="text-sm font-medium text-gray-900 mb-1">No Artists Linked</h3>
                  <p className="text-xs text-gray-500 mb-4">Link your Spotify artist profile to track metrics</p>
                  <button
                    onClick={() => setShowArtistSearch(true)}
                    className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                  >
                    <PlusCircleIcon className="h-4 w-4 mr-1" />
                    Link Artist
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;